import { useState, useEffect } from 'react';
import { useTheme } from '../styles/ThemeContext';

const API_BASE = window.TMS_API_BASE || '';

/**
 * Shown when user visits the app with ?activate=TOKEN in the URL.
 * Validates the token and lets the user set their password.
 */
export function ActivationModal({ token, onDone }) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [username, setUsername] = useState('');
  const [status, setStatus] = useState('loading'); // loading | form | success | error
  const [errMsg, setErrMsg] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/activate/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok) {
          setUsername(data.username);
          setStatus('form');
        } else {
          setErrMsg(data.error || 'Invalid activation link');
          setStatus('error');
        }
      })
      .catch(() => {
        setErrMsg('Could not connect to server');
        setStatus('error');
      });
  }, [token]);

  const passwordRules = [
    { label: 'At least 8 characters',        test: pw => pw.length >= 8 },
    { label: 'At least 1 uppercase letter',   test: pw => /[A-Z]/.test(pw) },
    { label: 'At least 1 number',             test: pw => /[0-9]/.test(pw) },
    { label: 'At least 1 special character',  test: pw => /[^A-Za-z0-9]/.test(pw) },
  ];
  const passwordValid = passwordRules.every(r => r.test(password));

  const handleSubmit = async () => {
    if (!passwordValid) { setErrMsg('Password does not meet all requirements'); return; }
    if (password !== password2) { setErrMsg('Passwords do not match'); return; }
    setErrMsg('');
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/activate/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
      } else {
        setErrMsg(data.error || 'Failed');
      }
    } catch {
      setErrMsg('Could not connect to server');
    } finally {
      setSaving(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Segoe UI', sans-serif"
  };

  const card = {
    background: c.bgCard,
    border: '1px solid ' + c.border,
    borderRadius: 8,
    padding: '32px 36px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 10px 40px rgba(0,0,0,0.4)'
  };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
          <div style={{ fontWeight: 700, fontSize: 18, color: c.text }}>TMS Packing</div>
          <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4 }}>Account Activation</div>
        </div>

        {status === 'loading' && (
          <div style={{ textAlign: 'center', color: c.textSecondary, fontSize: 13 }}>
            Validating link…
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: c.error, fontSize: 13, marginBottom: 20 }}>{errMsg}</div>
            <button onClick={onDone} style={{ ...theme.btnP, width: '100%' }}>Go to login</button>
          </div>
        )}

        {status === 'form' && (
          <div>
            <div style={{ fontSize: 13, color: c.textSecondary, marginBottom: 20, textAlign: 'center' }}>
              Welcome <strong style={{ color: c.text }}>{username}</strong>! Choose a password to activate your account.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ ...theme.label, display: 'block', marginBottom: 4 }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setErrMsg(''); }}
                style={{ ...theme.inp, width: '100%', boxSizing: 'border-box' }}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {passwordRules.map(r => {
                  const ok = r.test(password);
                  return (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                      <span style={{ color: ok ? c.success : c.textSecondary }}>{ok ? '✓' : '○'}</span>
                      <span style={{ color: ok ? c.success : c.textSecondary }}>{r.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...theme.label, display: 'block', marginBottom: 4 }}>Confirm password</label>
              <input
                type="password"
                value={password2}
                onChange={e => { setPassword2(e.target.value); setErrMsg(''); }}
                style={{ ...theme.inp, width: '100%', boxSizing: 'border-box' }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              />
            </div>
            {errMsg && <div style={{ color: c.error, fontSize: 12, marginBottom: 12 }}>{errMsg}</div>}
            <button
              onClick={handleSubmit}
              disabled={saving || !passwordValid || !password2}
              style={{ ...theme.btnP, width: '100%', opacity: (saving || !passwordValid || !password2) ? 0.4 : 1, cursor: saving ? 'wait' : 'pointer' }}
            >
              {saving ? 'Activating…' : 'Activate account'}
            </button>
          </div>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontWeight: 600, color: c.text, marginBottom: 8 }}>Account activated!</div>
            <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 20 }}>
              Your password has been set. You can now log in.
            </div>
            <button onClick={onDone} style={{ ...theme.btnP, width: '100%' }}>Go to login</button>
          </div>
        )}
      </div>
    </div>
  );
}
